"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  EmblaOptionsType,
  EmblaCarouselType,
  EmblaEventType,
  EmblaPluginType,
} from "embla-carousel";

import Autoplay, { AutoplayOptionsType } from "embla-carousel-autoplay";
import { twMerge } from "tailwind-merge";
import { DotPagination, useDotPagination } from "./DotPagination";
import { Button } from "../button/Button";
import { iconList } from "@/assets/icons";
import { Skeleton } from "../skelton";
import { cn } from "@/lib/utils";

const TWEEN_FACTOR_BASE = 0.52;

// Ensures a number stays within a specific range
const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

interface IEmblaCarouselProps {
  children: React.ReactNode; // Array of slide content
  options?: EmblaOptionsType; // Embla core options
  autoplayOptions?: AutoplayOptionsType; // Autoplay plugin options
  plugins?: EmblaPluginType[]; // Array of Embla plugins
  containerClassName?: string; // Custom class for the carousel container
  onInit?: (embla: EmblaCarouselType) => void; // Callback on carousel init
  onSlideChange?: (embla: EmblaCarouselType) => void; // Callback on slide change
  showPagination?: boolean;
  showNavigation?: boolean;
  showNavigationCounter?: boolean; // Show/hide the "1/10" counter
  enableScaleAnimation?: boolean; // New prop to enable/disable scale animation
  scaleTargetSelector?: string; // Selector for the element to apply scale to
  limitNavigation?: number; // Config case 3 item incorrect view
  configOverflow?: boolean;
  loop?: boolean;
  navigationClassNames?: {
    prev?: string;
    next?: string;
    container?:string;
  };
  minScale?: number;
  scaleStep?: number; // Decrease scale by {scaleStep} for each additional step away from center
  navigationContent?: {
    prev?: React.ReactNode; 
    next?: React.ReactNode; 
  };
  showDotIndicators?: boolean; 
  dotIndicatorsContainerClassName?: string; 
  dotIndicatorClassName?: string; 
  activeDotClassName?: string; 
}

/**
 * EmblaCarousel Component
 *
 * A customizable, feature-rich carousel built on top of Embla.
 * Supports:
 * - Autoplay
 * - Dot pagination
 * - Navigation buttons with custom content
 * - Dot indicators with custom styling
 * - Optional scale animation for active/adjacent slides
 */
const EmblaCarousel = (props: IEmblaCarouselProps) => {
  const {
    options = {},
    autoplayOptions,
    plugins = [],
    containerClassName = "",
    onInit,
    onSlideChange,
    children,
    showPagination = false,
    showNavigation = true,
    showNavigationCounter = true,
    enableScaleAnimation = false,
    scaleTargetSelector = ".embla__slide__content",
    limitNavigation = 0,
    configOverflow = false,
    loop = true,
    navigationClassNames = {},
    minScale = 0.8,
    scaleStep,
    navigationContent,
    showDotIndicators = false,
    dotIndicatorsContainerClassName = "",
    dotIndicatorClassName = "",
    activeDotClassName = "",
  } = props;
  
  const prevClassName = navigationClassNames.prev || "custom-prev";
  const nextClassName = navigationClassNames.next || "custom-next";
  const navigationContainerClassNames=navigationClassNames.container

  const tweenFactor = useRef(0);
  const tweenNodes = useRef<HTMLElement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Initialize Embla with optional autoplay and other plugins
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop,
      align: "center",
      skipSnaps: true,
      ...options,
    },
    [...(autoplayOptions ? [Autoplay(autoplayOptions)] : []), ...plugins],
  );
  
  const { selectedIndex, scrollSnaps, onDotPaginationClick } =
    useDotPagination(emblaApi);
  const { SliderNext, SliderPrev } = iconList;

  // Carousel navigation
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // Merge container classes dynamically
  const _containerClassName = useMemo(() => {
    return twMerge(
      "flex items-stretch touch-pan-y touch-pinch-zoom",
      containerClassName,
    );
  }, [containerClassName]);

  // Merge dot indicators container classes
  const _dotIndicatorsContainerClassName = useMemo(() => {
    return twMerge(
      "flex items-center justify-center gap-2 mt-4",
      dotIndicatorsContainerClassName,
    );
  }, [dotIndicatorsContainerClassName]);

  // Merge dot indicator classes
  const getDotClassName = useCallback((isActive: boolean) => {
    const baseClass = dotIndicatorClassName || "h-2 w-2 rounded-full bg-gray-300 cursor-pointer transition-all duration-300";
    const activeClass = activeDotClassName || "bg-primary-950 w-8";
    return twMerge(baseClass, isActive ? activeClass : "");
  }, [dotIndicatorClassName, activeDotClassName]);

  // Collect all elements that will be affected by the scale animation
  const setTweenNodes = useCallback(
    (emblaApi: EmblaCarouselType): void => {
      tweenNodes.current = emblaApi.slideNodes().map((slideNode) => {
        const targetElement = slideNode.querySelector(
          scaleTargetSelector,
        ) as HTMLElement;
        return targetElement || slideNode;
      });
    },
    [scaleTargetSelector],
  );

  // Compute tween factor based on total slide count
  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  /**
   * Scale animation handler
   * Adjusts slide scale dynamically based on distance from the active slide.
   */
  const tweenScale = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      if (!enableScaleAnimation) return;

      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === "scroll";
      const scrollSnapList = emblaApi.scrollSnapList();
      const snapCount = scrollSnapList.length;

      // Helper: adjust diff for looping
      const adjustLoopDiff = (slideIndex: number, diff: number) => {
        if (!engine.options.loop) return diff;
        for (const loopItem of engine.slideLooper.loopPoints) {
          const target = loopItem.target();
          if (slideIndex === loopItem.index && target !== 0) {
            const sign = Math.sign(target);
            if (sign === -1)
              return scrollSnapList[slideIndex] - (1 + scrollProgress);
            if (sign === 1)
              return scrollSnapList[slideIndex] + (1 - scrollProgress);
          }
        }
        return diff;
      };

      // Helper: calculate scale for a slide
      const calculateScale = (diff: number) => {
        if (scaleStep) {
          const distance = Math.round(Math.abs(diff * snapCount));
          if (distance === 0) return 1;
          const s = minScale - (distance - 1) * scaleStep;
          return Math.max(0.5, s);
        } else {
          const tweenValue = 1 - Math.abs(diff * tweenFactor.current);
          return numberWithinRange(tweenValue, minScale, 1);
        }
      };

      // Helper: apply scale to DOM node
      const applyScale = (slideIndex: number, scale: number) => {
        const node = tweenNodes.current[slideIndex];
        if (!node) return;
        node.style.transform = `scale(${scale})`;
        node.style.transformOrigin = "center center";
        node.style.transition = isScrollEvent
          ? "none"
          : "transform 0.3s ease-out";
      };

      // Main loop
      for (let snapIndex = 0; snapIndex < snapCount; snapIndex++) {
        const scrollSnap = scrollSnapList[snapIndex];
        const slidesInSnap = engine.slideRegistry[snapIndex];

        for (const slideIndex of slidesInSnap) {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) continue;

          let diffToTarget = scrollSnap - scrollProgress;
          diffToTarget = adjustLoopDiff(slideIndex, diffToTarget);

          const scale = calculateScale(diffToTarget);
          applyScale(slideIndex, scale);
        }
      }
    },
    [enableScaleAnimation, minScale, scaleStep],
  );

  // Initialize carousel and set up listeners
  useEffect(() => {
    if (!emblaApi) return;

    setIsLoading(false);
    // Call onInit callback
    onInit?.(emblaApi);

    // Initialize scale animation if enabled
    if (enableScaleAnimation) {
      setTweenNodes(emblaApi);
      setTweenFactor(emblaApi);
      tweenScale(emblaApi);

      emblaApi
        .on("reInit", setTweenNodes)
        .on("reInit", setTweenFactor)
        .on("reInit", tweenScale)
        .on("scroll", tweenScale)
        .on("slideFocus", tweenScale);
    }

    // Handle slide change events
    const onSelect = () => onSlideChange?.(emblaApi);
    emblaApi.on("select", onSelect);

    // Cleanup
    return () => {
      emblaApi.off("select", onSelect);
      if (enableScaleAnimation) {
        emblaApi
          .off("reInit", setTweenNodes)
          .off("reInit", setTweenFactor)
          .off("reInit", tweenScale)
          .off("scroll", tweenScale)
          .off("slideFocus", tweenScale);
      }
    };
  }, [
    emblaApi,
    onInit,
    onSlideChange,
    enableScaleAnimation,
    setTweenNodes,
    setTweenFactor,
    tweenScale,
  ]);

  // Default navigation content
  const defaultPrevContent = (
    <>
      <SliderPrev />
      <span className="text-primary-950 font-[var(--font-medium)]">前へ</span>
    </>
  );

  const defaultNextContent = (
    <>
      <span className="text-primary-950 font-[var(--font-medium)]">次へ</span>
      <SliderNext />
    </>
  );
 
  return (
    <div className="mx-auto w-full">
      <div className={`${configOverflow ? "overflow-hidden py-1" : ""}`} ref={emblaRef}>
        {isLoading ? (
          <div className="flex justify-center items-center gap-6 w-full py-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                className="h-[280px] w-[300px] md:h-[300px] md:w-[360px] rounded-xl 
                           bg-neutral-100/80 dark:bg-neutral-300/30 shadow-[0_0_6px_rgba(0,0,0,0.05)]"
              />
            ))}
          </div>
        ) : (
          <div className={_containerClassName}>{children}</div>
        )}
      </div>

      {/* Dot Indicators */}
      {showDotIndicators && (
        <div className={_dotIndicatorsContainerClassName}>
          {Array.from({ length: limitNavigation || scrollSnaps.length }).map(
            (_, index) => {
              const isActive = limitNavigation
                ? selectedIndex % limitNavigation === index
                : index === selectedIndex;
              
              return (
                <button
                  key={index}
                  onClick={() => onDotPaginationClick(index)}
                  className={getDotClassName(isActive)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              );
            },
          )}
        </div>
      )}

      {/* Legacy Pagination (kept for backward compatibility) */}
      {showPagination && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {Array.from({ length: limitNavigation || scrollSnaps.length }).map(
            (_, index) => {
              const dotPaginationKey = `carousel-slide-${index}`;
              const isActive = limitNavigation
                ? selectedIndex % limitNavigation === index
                : index === selectedIndex;
              return (
                <DotPagination
                  key={dotPaginationKey}
                  onClick={() => onDotPaginationClick(index)}
                  className={
                    "border-primary-950 h-3 w-3 cursor-pointer rounded-full border" +
                    (isActive ? " bg-primary-950" : "")
                  }
                />
              );
            },
          )}
        </div>
      )}

      {/* Navigation Buttons */}
      {showNavigation && !isLoading  && (
        <div className={cn("xs:block flex transform items-center justify-center gap-4 rounded-lg py-10 text-white",navigationContainerClassNames)}>
          <Button
            onClick={scrollPrev}
            hierarchy={"outline"}
            className={`${prevClassName} cursor-pointer rounded-full border-0 bg-transparent !px-1 !py-0 transition hover:bg-transparent active:bg-transparent`}
          >
            {navigationContent?.prev || defaultPrevContent}
          </Button>
          {showNavigationCounter && (
            <span className="text-xs font-semibold text-[var(--text-base-primary)] lg:text-sm">
              {limitNavigation
                ? (selectedIndex % limitNavigation) + 1
                : selectedIndex + 1}
              /{limitNavigation || (scrollSnaps?.length ?? 0)}
            </span>
          )}
          <Button
            onClick={scrollNext}
            hierarchy={"outline"}
            className={`${nextClassName} cursor-pointer rounded-full border-0 bg-transparent !px-1 !py-0 transition hover:border-0 hover:bg-transparent active:bg-transparent`}
          >
            {navigationContent?.next || defaultNextContent}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmblaCarousel;